import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiPhone, FiBookOpen, FiMonitor, FiFileText, FiEye, FiSettings, FiLink, FiArrowRight } from 'react-icons/fi';
import { fetchAPI } from '../../utils/api';
import { motion, AnimatePresence } from 'framer-motion';

export default function StudentTeacherDetail() {
    const { teacherId } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('classes');
    const [teacher, setTeacher] = useState(null);
    const [loading, setLoading] = useState(true);
    const [previewPack, setPreviewPack] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    React.useEffect(() => {
        const loadTeacherDetails = async () => {
            try {
                const data = await fetchAPI(`/student/get_teacher_details.php?teacher_id=${teacherId}`);
                setTeacher(data);
            } catch (err) {
                console.error("Failed to load teacher details", err);
            } finally {
                setLoading(false);
            }
        };
        loadTeacherDetails();
    }, [teacherId]);

    const handlePreviewPack = async (pack) => {
        setPreviewPack(pack);
        setPreviewLoading(true);
        try {
            const data = await fetchAPI(`/admin/get_study_pack_details.php?id=${pack.id}`);
            setPreviewPack(data);
        } catch (err) {
            console.error("Failed to load pack content", err);
        } finally {
            setPreviewLoading(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500 font-bold">Loading teacher details...</div>;
    if (!teacher) return <div className="p-8 text-center text-red-500 font-bold">Teacher not found</div>;

    const { classes, study_packs } = teacher;

    return (
        <div className="space-y-6">
            {/* Banner */}
            {/* Banner - Hidden on Mobile */}
            <div className="hidden lg:block w-full h-64 rounded-xl overflow-hidden relative shadow-lg">
                <img src={teacher.banner_url || 'https://www.freepik.com/free-photo/liquid-marbling-paint-texture-background-fluid-painting-abstract-texture-intensive-color-mix-wallpaper_26543298.htm#fromView=search&page=1&position=4&uuid=d6b7656d-1790-4c97-ab60-0f508298f9ab&query=backgrod'} alt="Subject Banner" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-transparent flex items-center px-12 bg-[url('https://www.freepik.com/free-photo/liquid-marbling-paint-texture-background-fluid-painting-abstract-texture-intensive-color-mix-wallpaper_26543298.htm#fromView=search&page=1&position=4&uuid=d6b7656d-1790-4c97-ab60-0f508298f9ab&query=backgrod')]">
                    <div className="text-white">
                        <p className="text-xl font-light opacity-90 mb-1">G. C. E. Ordinary Level</p>
                        <h1 className="text-6xl font-black tracking-tighter uppercase mb-2">{teacher.subject}</h1>
                        <p className="text-lg font-handwriting opacity-90">Unlock your potential with Maths with Manoj</p>
                    </div>
                </div>
            </div>

            {/* Content Split */}
            <div className="flex flex-col lg:flex-row gap-6">

                {/* Left: Teacher Profile - Hidden on Mobile */}
                <div className="hidden lg:block w-full lg:w-80 space-y-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center">
                        drum
                        <img
                            src={teacher.image_url || 'https://via.placeholder.com/150'}
                            alt={teacher.name}
                            className="w-32 h-32 rounded-full border-4 border-blue-50 mx-auto mb-4 object-cover"
                        />
                        <h2 className="text-xl font-bold text-gray-900">{teacher.name}</h2>
                        <p className="text-[10px] uppercase font-black text-blue-600 tracking-widest mb-2">{teacher.subject}</p>
                        <p className="text-xs text-gray-500 mt-2 mb-4 px-2 italic">{teacher.qualifications}</p>

                        <div className="flex items-center justify-center gap-2 text-gray-600 bg-gray-50 py-2 rounded-lg text-sm font-medium">
                            <FiPhone size={16} />
                            <span>{teacher.phone}</span>
                        </div>
                    </div>
                </div>

                {/* Right: Classes Grid */}
                <div className="flex-1">
                    {/* Tabs */}
                    <div className="flex border-b border-gray-200 mb-6">
                        <button
                            onClick={() => setActiveTab('classes')}
                            className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'classes'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Classes
                        </button>
                        <button
                            onClick={() => setActiveTab('packs')}
                            className={`px-6 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'packs'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Study Packs
                        </button>
                    </div>

                    {/* Content Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {activeTab === 'classes' && classes.map((cls) => (
                            <div key={cls.id} className="bg-white rounded-lg border border-gray-100 p-6 hover:shadow-md transition-shadow">
                                <div className="w-16 h-16 mx-auto bg-gray-50 rounded-xl flex items-center justify-center mb-4 text-gray-400">
                                    <FiMonitor size={32} />
                                </div>
                                <h3 className="text-sm font-bold text-gray-900 text-center mb-4 h-10 overflow-hidden">{cls.name}</h3>

                                <div className="space-y-2 mb-6 text-center">
                                    <p className="text-xs text-blue-600 font-bold uppercase">{cls.type} • Grade {cls.grade}</p>
                                    <p className="text-xs font-bold text-gray-700">LKR {cls.fee}</p>
                                </div>

                                <button
                                    onClick={() => navigate(`/dashboard/courses/class/${cls.id}`)}
                                    className="w-full py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition-colors uppercase shadow-lg shadow-blue-100"
                                >
                                    See Classes
                                </button>
                            </div>
                        ))}

                        {activeTab === 'packs' && study_packs.map((pack) => (
                            <div key={pack.id} className="group bg-white rounded-xl border border-gray-100 p-6 hover:shadow-2xl hover:shadow-blue-900/5 transition-all duration-500 relative overflow-hidden font-display">
                                {/* Abstract Decor */}
                                <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-50/50 rounded-full blur-2xl group-hover:bg-blue-100 transition-colors"></div>

                                {/* Badge */}
                                <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-50 rounded-full mb-4 relative z-10">
                                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                                    <span className="text-[8px] font-black text-green-600 uppercase tracking-widest">FREE ACCESS</span>
                                </div>

                                <div className="relative z-10">
                                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-tight mb-1 group-hover:text-blue-600 transition-colors line-clamp-2 min-h-[40px]">
                                        {pack.title}
                                    </h3>
                                    <p className="text-[10px] text-gray-400 mb-4 line-clamp-1 italic font-medium">{pack.description}</p>

                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="flex items-center gap-1.5 text-blue-600/60">
                                            <FiMonitor size={12} />
                                            <span className="text-[9px] font-black uppercase tracking-widest">{pack.lessons_count || 0} Lessons</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 text-indigo-600/60">
                                            <FiFileText size={12} />
                                            <span className="text-[9px] font-black uppercase tracking-widest">{pack.tutes_count || 0} Tutes</span>
                                        </div>
                                    </div>

                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handlePreviewPack(pack)}
                                            className="flex-1 py-3 bg-gray-50 text-gray-400 text-[9px] font-black rounded-xl hover:bg-gray-100 hover:text-gray-600 transition-all uppercase tracking-widest"
                                        >
                                            Preview
                                        </button>
                                        <button
                                            onClick={() => navigate(`/dashboard/study-packs/view/${pack.id}`)}
                                            className="flex-[1.5] py-3 bg-gray-900 text-white text-[9px] font-black rounded-xl hover:bg-blue-600 transition-all uppercase tracking-widest shadow-xl shadow-gray-900/10 hover:shadow-blue-600/20 flex items-center justify-center gap-2"
                                        >
                                            Open <FiArrowRight size={12} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {(activeTab === 'classes' && classes.length === 0) && (
                            <div className="col-span-full py-12 text-center text-gray-400 font-medium">No classes yet.</div>
                        )}
                        {(activeTab === 'packs' && study_packs.length === 0) && (
                            <div className="col-span-full py-12 text-center text-gray-400 font-medium">No study packs available.</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Preview Modal */}
            <AnimatePresence>
                {previewPack && (
                    <div className="fixed inset-0 z-[3000] flex items-center justify-center p-6">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setPreviewPack(null)}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        ></motion.div>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden font-display"
                        >
                            <div className="p-8 md:p-12">
                                <h2 className="text-2xl font-black text-gray-900 italic uppercase tracking-tight mb-2">
                                    Pack <span className="text-blue-600">Contents</span>
                                </h2>
                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-8">Subject: {previewPack.subject}</p>

                                {previewLoading ? (
                                    <div className="py-20 flex flex-col items-center justify-center gap-4">
                                        <div className="w-8 h-8 border-2 border-blue-500/20 border-t-blue-600 rounded-full animate-spin"></div>
                                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Loading Payload...</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3 max-h-[400px] overflow-y-auto pr-4 custom-scrollbar">
                                        {previewPack.contents?.map((item, idx) => (
                                            <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100 group">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-blue-600 shadow-sm">
                                                        {item.type === 'pdf' ? <FiFileText size={18} /> : item.type === 'recording' ? <FiMonitor size={18} /> : <FiLink size={18} />}
                                                    </div>
                                                    <div>
                                                        <h4 className="text-xs font-bold text-gray-900 uppercase tracking-tight">{item.title}</h4>
                                                        <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest">{item.type}</span>
                                                    </div>
                                                </div>
                                                {item.is_free ? (
                                                    <a
                                                        href={item.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="px-4 py-2 bg-green-500 text-white text-[10px] font-black rounded-lg hover:bg-green-600 transition-all uppercase tracking-widest shadow-lg shadow-green-100 flex items-center gap-2"
                                                    >
                                                        Access Free <FiEye />
                                                    </a>
                                                ) : (
                                                    <div className="flex items-center gap-2 text-gray-400">
                                                        <FiSettings size={12} className="animate-spin-slow" />
                                                        <span className="text-[8px] font-black uppercase tracking-widest">Locked</span>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        {(!previewPack.contents || previewPack.contents.length === 0) && (
                                            <p className="text-center py-12 text-gray-400 text-[10px] uppercase font-black tracking-widest">No entries in this module payload yet.</p>
                                        )}
                                    </div>
                                )}

                                <div className="flex gap-4 pt-8 mt-6 border-t border-gray-50">
                                    <button
                                        onClick={() => setPreviewPack(null)}
                                        className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-900 font-black uppercase tracking-widest py-4 rounded-2xl transition-all"
                                    >
                                        Close
                                    </button>
                                    {!previewLoading && previewPack.fee > 0 && (
                                        <button
                                            onClick={() => navigate(`/dashboard/payments?pack_id=${previewPack.id}`)}
                                            className="flex-[1.5] bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-widest py-4 rounded-2xl transition-all shadow-lg shadow-blue-600/20"
                                        >
                                            Purchase Full Module
                                        </button>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
